import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';

const InspectorDashboard = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('assigned'); // assigned, completed, all
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState('');

  const fetchInspections = useCallback(async () => {
    try {
      const response = await api.get('/inspections/inspector', {
        params: { status: filter === 'all' ? undefined : filter }
      });
      setInspections(response.data.data);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      setError('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const handleStatusUpdate = async (inspectionId, newStatus) => {
    try {
      await api.put(`/inspections/inspector/${inspectionId}/status`, {
        status: newStatus
      });

      // Refresh inspections
      fetchInspections();
    } catch (error) {
      console.error('Error updating inspection status:', error);
      setError('Failed to update inspection status');
    }
  };

  const handleFileUpload = async (inspectionId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('report', file);

    try {
      setUploading(inspectionId);
      setError('');

      await api.post(`/inspections/inspector/${inspectionId}/report`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update status to completed and refresh
      await handleStatusUpdate(inspectionId, 'completed');
    } catch (error) {
      console.error('Error uploading report:', error);
      setError('Failed to upload report');
    } finally {
      setUploading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'assigned':
        return 'Assigned';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Inspector Dashboard</h1>
        <p className="mt-2 text-gray-600">Manage your assigned inspections and upload reports</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'assigned', label: 'Assigned' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'completed', label: 'Completed' },
              { key: 'all', label: 'All Inspections' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Inspections List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {inspections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No inspections found for the selected filter.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {inspections.map((inspection) => (
              <li key={inspection._id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {inspection.inspectionMonth} Inspection
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {inspection.propertyAddress}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {inspection.propertyType} • Customer: {inspection.customer?.name || 'N/A'}
                        </p>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inspection.status)}`}>
                          {getStatusText(inspection.status)}
                        </span>
                      </div>
                    </div>

                    {inspection.scheduledDate && (
                      <div className="mt-2 text-sm text-gray-600">
                        Scheduled: {format(new Date(inspection.scheduledDate), 'PPP')} at {inspection.scheduledTime}
                      </div>
                    )}

                    {inspection.completedAt && (
                      <div className="mt-2 text-sm text-gray-600">
                        Completed: {format(new Date(inspection.completedAt), 'PPP')}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    {/* Status Update Buttons */}
                    {inspection.status === 'assigned' && (
                      <button
                        onClick={() => handleStatusUpdate(inspection._id, 'in_progress')}
                        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700"
                      >
                        Start Inspection
                      </button>
                    )}

                    {inspection.status === 'in_progress' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(inspection._id, e.target.files[0])}
                          className="hidden"
                          id={`report-${inspection._id}`}
                        />
                        <label
                          htmlFor={`report-${inspection._id}`}
                          className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 cursor-pointer"
                        >
                          {uploading === inspection._id ? 'Uploading...' : 'Upload Report'}
                        </label>
                      </div>
                    )}

                    {/* View Details Button */}
                    <button
                      onClick={() => navigate(`/inspector/inspections/${inspection._id}`)}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm hover:bg-gray-200"
                    >
                      View Details
                    </button>
                  </div>
                </div>

                {/* Report Link */}
                {inspection.reportFile && (
                  <div className="mt-3">
                    <a
                      href={`${process.env.REACT_APP_API_URL}/uploads/${inspection.reportFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View Report
                    </a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">A</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Assigned</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inspections.filter(i => i.status === 'assigned').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">P</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inspections.filter(i => i.status === 'in_progress').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">C</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inspections.filter(i => i.status === 'completed').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                  <dd className="text-lg font-medium text-gray-900">{inspections.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectorDashboard;